import { useEffect, useRef } from "react";

import type { HeroLatticeData, HeroPointer } from "@/landing/lib/hero-lattice";
import {
  createHeroLattice,
  heroFirstPaintScript,
} from "@/landing/lib/hero-lattice";
import { CAT_MARK_PATHS, CAT_PUPILS, CAT_ROWS } from "@/landing/lib/pixel-cat";
import { AwardBadge, EmailJoinForm, TrustLine } from "@/landing/system/actions";
import { AgentLogos } from "@/landing/system/marks";

const LATTICE: HeroLatticeData = {
  rows: CAT_ROWS,
  marks: [...CAT_MARK_PATHS],
  pupils: CAT_PUPILS,
};
// Painted inline during parsing; the effect below takes over after hydration.
const FIRST_PAINT = heroFirstPaintScript(LATTICE);

/**
 * Homepage hero: Wonda's pixel cat drawn in tiny LinkedIn, Sales Navigator, X
 * and Reddit marks on a dot lattice (marblist-style), the copy bottom-left.
 * The whole hero is one screen with no scroll; on phones the cat takes the
 * space the copy leaves under the nav. The cat is static; the pointer only
 * nudges the marks aside.
 */
export function AgenticBrowserHero() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const canvas = canvasRef.current;
    const slot = slotRef.current;
    if (!section || !canvas || !slot) return;
    const lattice = createHeroLattice(section, canvas, slot, LATTICE);
    if (!lattice) return;
    // Tells the inline first paint to stop repainting.
    canvas.dataset.live = "";

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const pointer: HeroPointer = { x: -9999, y: -9999, on: 0, target: 0 };
    let frame = 0;
    const layout = () => lattice.layout();
    const draw = () => lattice.draw(pointer);

    function tick() {
      frame = draw() ? requestAnimationFrame(tick) : 0;
    }
    function wake() {
      if (!frame) frame = requestAnimationFrame(tick);
    }

    function onPointerMove(event: PointerEvent) {
      if (!section || reduceMotion) return;
      const rect = section.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      pointer.target = 1;
      wake();
    }
    function onPointerLeave() {
      pointer.target = 0;
      wake();
    }

    layout();
    draw();
    let resizeTimer = 0;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        layout();
        draw();
      }, 120);
    });
    observer.observe(section);
    observer.observe(slot);
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", onPointerLeave);
    window.addEventListener("blur", onPointerLeave);

    return () => {
      observer.disconnect();
      window.clearTimeout(resizeTimer);
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onPointerMove);
      document.documentElement.removeEventListener(
        "mouseleave",
        onPointerLeave,
      );
      window.removeEventListener("blur", onPointerLeave);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative isolate -mt-(--header-height) flex min-h-svh flex-col overflow-hidden pt-(--header-height) text-(--g-ink) md:block md:pt-0"
      style={{
        background:
          "radial-gradient(ellipse 80% 70% at 50% 44%, var(--g-lit) 0%, transparent 70%), radial-gradient(ellipse 140% 120% at 50% 50%, transparent 55%, var(--g-shade) 100%), var(--g-card)",
      }}
    >
      <div
        aria-hidden
        className="g-grain pointer-events-none absolute inset-0 -z-20 opacity-50"
      />
      <canvas
        ref={canvasRef}
        aria-hidden
        className="absolute inset-0 -z-10 block size-full"
      />

      {/* Phones: the room between the nav and the copy, which the cat fills. */}
      <div
        ref={slotRef}
        aria-hidden
        data-hero-slot=""
        className="my-3 min-h-0 flex-1 md:hidden"
      />

      <div className="relative px-4 pb-6 md:absolute md:bottom-(--g-margin) md:left-(--g-margin) md:max-w-[min(43rem,47vw)] md:p-0">
        <AwardBadge className="mb-4 md:mb-[26px]" />

        <h1 className="m-0 text-[min(40px,10vw)] leading-none font-medium tracking-[-0.035em] text-balance md:text-[clamp(40px,4.8vw,70px)]">
          The agentic browser for social media.
        </h1>

        <p className="mt-4 max-w-[32em] text-[15px] leading-[1.45] text-pretty text-(--g-ink-2) md:mt-6 md:text-[clamp(15px,1.1vw,17px)]">
          Your agent works in the background on LinkedIn, X and Reddit to find
          profiles, message, post <em className="text-(--g-ink)">exactly</em>{" "}
          like you’re doing manually.
        </p>

        <TrustLine className="mt-3 md:mt-[18px]" />
        <EmailJoinForm className="mt-5 md:mt-[34px]" />
        <AgentLogos className="mt-4 md:mt-[22px]" />
      </div>

      {/* After the copy, so the phone slot already has its height. */}
      <script
        dangerouslySetInnerHTML={{ __html: FIRST_PAINT }}
        suppressHydrationWarning
      />
    </section>
  );
}
